/**
 * useEpisodesState - Custom hook for Episodes module state management
 * Extracted from Admin.jsx to reduce state complexity
 * 
 * No changes to behavior, API endpoints, or request payloads.
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../utils/apiUrl';

const getAuthHeaders = () => {
  const token = sessionStorage.getItem('golfnuts_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const initialEpisodeForm = {
  title: '',
  description: '',
  series: 'Main Show',
  duration: 'TBA',
  publishDate: new Date().toISOString().split('T')[0],
  slug: '',
  thumbnail: '',
  youtubeId: '',
  spotifyUrl: '',
  applePodcastsUrl: '',
  showComingSoon: false,
  isNew: true
};

export const useEpisodesState = ({ setError, setSuccess }) => {
  // Core episodes state
  const [cmsEpisodes, setCmsEpisodes] = useState([]);
  const [loadingEpisodes, setLoadingEpisodes] = useState(false);
  const [editingEpisode, setEditingEpisode] = useState(null);
  const [showEpisodeModal, setShowEpisodeModal] = useState(false);
  const [episodeModalMode, setEpisodeModalMode] = useState('edit'); // 'edit' | 'chooseMedia' | 'addGuest'

  // Inline media picker state for episode modal
  const [episodeMediaList, setEpisodeMediaList] = useState([]);
  const [episodeMediaFilter, setEpisodeMediaFilter] = useState('all');
  const [episodeMediaLoading, setEpisodeMediaLoading] = useState(false);
  const [episodeSelectedMedia, setEpisodeSelectedMedia] = useState(null);

  // Fetch all episodes
  const fetchCmsEpisodes = useCallback(async () => {
    setLoadingEpisodes(true);
    try {
      const response = await axios.get(`${API_URL}/api/cms/episodes?include_hidden=true`);
      // Sort episodes by release date (publishDate) descending (newest first)
      const sortedEpisodes = (response.data || []).sort((a, b) => {
        const dateA = a.publishDate ? new Date(a.publishDate) : new Date(0);
        const dateB = b.publishDate ? new Date(b.publishDate) : new Date(0);
        return dateB - dateA; // Descending order
      });
      setCmsEpisodes(sortedEpisodes);
    } catch (err) {
      console.error('Error fetching episodes:', err);
      setError('Failed to load episodes');
    } finally {
      setLoadingEpisodes(false);
    }
  }, [setError]);

  // Set episode as featured in hero
  const handleSetFeaturedHero = useCallback(async (episodeId, episodeTitle) => {
    try {
      await axios.put(`${API_URL}/api/cms/episodes/${episodeId}/set-featured-hero`, {}, {
        headers: getAuthHeaders()
      });
      setSuccess(`"${episodeTitle}" is now featured in the hero section!`);
      fetchCmsEpisodes(); // Refresh to show updated state
    } catch (err) {
      setError('Failed to feature episode: ' + (err.response?.data?.detail || err.message));
    }
  }, [fetchCmsEpisodes, setError, setSuccess]);

  // Remove episode from featured hero
  const handleRemoveFeaturedHero = useCallback(async (episodeId) => {
    try {
      await axios.put(`${API_URL}/api/cms/episodes/${episodeId}/remove-featured-hero`, {}, {
        headers: getAuthHeaders()
      });
      setSuccess('Episode removed from hero section');
      fetchCmsEpisodes();
    } catch (err) {
      setError('Failed to remove featured episode: ' + (err.response?.data?.detail || err.message));
    }
  }, [fetchCmsEpisodes, setError, setSuccess]);

  // Create new episode
  const handleCreateEpisode = useCallback(() => {
    setEditingEpisode({ ...initialEpisodeForm });
    setEpisodeModalMode('edit');
    setShowEpisodeModal(true);
  }, []);

  // Edit existing episode
  const handleEditEpisode = useCallback((episode) => {
    setEditingEpisode({ ...episode });
    setEpisodeModalMode('edit'); // Reset to edit mode
    setEpisodeSelectedMedia(null);
    setShowEpisodeModal(true);
  }, []);

  // Delete episode
  const handleDeleteEpisode = useCallback(async (episodeId, episodeTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${episodeTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/cms/episodes/${episodeId}`, {
        headers: getAuthHeaders()
      });
      setSuccess(`"${episodeTitle}" deleted successfully`);
      fetchCmsEpisodes();
    } catch (err) {
      setError('Failed to delete episode: ' + (err.response?.data?.detail || err.message));
    }
  }, [fetchCmsEpisodes, setError, setSuccess]);

  // Duplicate episode
  const handleDuplicateEpisode = useCallback(async (episode) => {
    try {
      // Create a copy of the episode with a new title and without the ID
      const duplicatedEpisode = {
        ...episode,
        title: `${episode.title} (Copy)`,
        youtubeId: '', // Clear YouTube ID so it's unreleased
        isFeaturedInHero: false, // Don't feature the duplicate
      };
      // Remove fields that shouldn't be copied
      delete duplicatedEpisode.id;
      delete duplicatedEpisode.createdAt;
      delete duplicatedEpisode.updatedAt;

      await axios.post(`${API_URL}/api/cms/episodes`, duplicatedEpisode, {
        headers: getAuthHeaders()
      });
      setSuccess(`Episode duplicated as "${duplicatedEpisode.title}"`);
      fetchCmsEpisodes();
    } catch (err) {
      setError('Failed to duplicate episode: ' + (err.response?.data?.detail || err.message));
    }
  }, [fetchCmsEpisodes, setError, setSuccess]);

  // Save episode (create or update)
  const handleSaveEpisode = useCallback(async () => {
    if (!editingEpisode) return;

    // Validation
    if (!editingEpisode.title?.trim()) {
      setError('Episode title is required');
      return;
    }

    try {
      if (editingEpisode.isNew) {
        // Create new episode
        const { isNew, ...episodeData } = editingEpisode;
        await axios.post(`${API_URL}/api/cms/episodes`, episodeData, {
          headers: getAuthHeaders()
        });
        setSuccess(`"${editingEpisode.title}" created successfully!`);
      } else {
        // Update existing episode
        await axios.put(`${API_URL}/api/cms/episodes/${editingEpisode.id}`, editingEpisode, {
          headers: getAuthHeaders()
        });
        setSuccess(`"${editingEpisode.title}" saved successfully!`);
      }
      setShowEpisodeModal(false);
      setEditingEpisode(null);
      fetchCmsEpisodes();
    } catch (err) {
      setError('Failed to save episode: ' + (err.response?.data?.detail || err.message));
    }
  }, [editingEpisode, fetchCmsEpisodes, setError, setSuccess]);

  // Handle field changes in edit form
  const handleEpisodeFieldChange = useCallback((field, value) => {
    setEditingEpisode(prev => prev ? ({ ...prev, [field]: value }) : null);
  }, []);

  // Open media picker within episode modal
  const handleOpenEpisodeMediaPicker = useCallback(async () => {
    setEpisodeMediaLoading(true);
    setEpisodeSelectedMedia(null);
    setEpisodeModalMode('chooseMedia');
    try {
      const response = await axios.get(`${API_URL}/api/cms/media`);
      setEpisodeMediaList(response.data || []);
    } catch (err) {
      console.error('Error fetching media:', err);
    } finally {
      setEpisodeMediaLoading(false);
    }
  }, []);

  // Select media item for episode thumbnail
  const handleEpisodeMediaSelect = useCallback((mediaItem) => {
    setEditingEpisode(prev => prev ? ({ ...prev, thumbnail: mediaItem.url }) : null);
    setEpisodeModalMode('edit');
    setEpisodeSelectedMedia(null);
  }, []);

  // Back to episode edit from media picker
  const handleBackToEpisodeEdit = useCallback(() => {
    setEpisodeModalMode('edit');
    setEpisodeSelectedMedia(null);
  }, []);

  // Get filtered episode media based on current filter
  const getFilteredEpisodeMedia = useCallback(() => {
    if (episodeMediaFilter === 'all') return episodeMediaList;
    return episodeMediaList.filter(m => m.category === episodeMediaFilter);
  }, [episodeMediaFilter, episodeMediaList]);

  // Toggle episode hidden state
  const handleToggleHidden = useCallback(async (episodeId, episodeTitle, isCurrentlyHidden) => {
    try {
      await axios.put(`${API_URL}/api/cms/episodes/${episodeId}/toggle-hidden`, {}, {
        headers: getAuthHeaders()
      });
      setSuccess(isCurrentlyHidden ? `"${episodeTitle}" is now visible` : `"${episodeTitle}" is now hidden from the live site`);
      fetchCmsEpisodes();
    } catch (err) {
      setError('Failed to toggle visibility: ' + (err.response?.data?.detail || err.message));
    }
  }, [fetchCmsEpisodes, setError, setSuccess]);

  return {
    // State
    cmsEpisodes,
    loadingEpisodes,
    editingEpisode,
    showEpisodeModal,
    episodeModalMode,
    episodeMediaList,
    episodeMediaFilter,
    episodeMediaLoading,
    episodeSelectedMedia,

    // Setters
    setCmsEpisodes,
    setEditingEpisode,
    setShowEpisodeModal,
    setEpisodeModalMode,
    setEpisodeMediaList,
    setEpisodeMediaFilter,
    setEpisodeMediaLoading,
    setEpisodeSelectedMedia,

    // Actions
    fetchCmsEpisodes,
    handleSetFeaturedHero,
    handleRemoveFeaturedHero,
    handleCreateEpisode,
    handleEditEpisode,
    handleDeleteEpisode,
    handleDuplicateEpisode,
    handleSaveEpisode,
    handleEpisodeFieldChange,
    handleOpenEpisodeMediaPicker,
    handleEpisodeMediaSelect,
    handleBackToEpisodeEdit,
    getFilteredEpisodeMedia,
    handleToggleHidden,
  };
};

export default useEpisodesState;
